'use client';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import YoutubePlayIcon from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import { RecipeStepProps } from '@repo/ui/src/types/recipes';
import SuperSmokeIcon from '@repo/ui/src/components/Icons/SuperSmokeIcon';
import ProbeIcon from '@repo/ui/src/components/Icons/ProbeIcon';
import TimerIcon from '@repo/ui/src/components/Icons/TimerIcon';
import GrillIcon from '@repo/ui/src/components/Icons/GrillIcon';
import Image from '@repo/ui/src/components/Image';
import ProTip from './_ProTip';
import Markdown from '@repo/ui/src/components/Markdown';
import RecipeStepIngredient from './_RecipeStepIngredients';

interface RecipeProps {
  step: RecipeStepProps;
  index: number;
  unitOfMeasure: number;
}

const RecipeStep = ({
  step: {
    cookCycleInfo: { grillTemperature, probeTargetTemperature, cookingLength, superSmoke },
    assets,
    youtubeLink,
    instruction,
    Ingredients,
    proTip,
  },
  index,
  unitOfMeasure,
}: RecipeProps) => {
  const { push } = useModal(ModalID.video);
  const { t } = useTranslation();

  const stepImg = assets ? buildImageSrc(assets[0], 800, 2, 1) : '';
  const hasLinkOrAssets = youtubeLink || assets;

  const getYoutubeId = (contentUrl: string) => contentUrl.split('/').pop();

  const padTo2Digits = (num: number) => {
    return num.toString().padStart(2, '0');
  };

  const timeConvert = (totalMinutes: number) => {
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);
    return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
  };

  const totalMinutes = timeConvert(cookingLength);

  return (
    <div className="md:tw-grid tw-gap-4 tw-pt-8 tw-pb-4">
      <div>
        <p className="tra-text-web-heading-6 tw-mb-2">
          {t('recipeContent.stepLabel', undefined, { default: 'Step' })} {index + 1}
        </p>
        <div className="tw-flex">
          <Markdown bodySize="large" className='!tw-mb-0'>{instruction}</Markdown>
        </div>
        {(cookingLength || grillTemperature || probeTargetTemperature || superSmoke) &&
          <div className="tw-flex tw-flex-wrap tw-gap-y-4 tw-gap-x-4 sm:tw-gap-x-10 sm:tw-mt-8">
            {cookingLength && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <TimerIcon className="tw-h-12" />
                <p className="tw-text-web-body-s tw-font-bold tw-mb-0">{totalMinutes}</p>
              </div>
            )}
            {grillTemperature && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <GrillIcon className="tw-h-12" />
                <p className="tw-text-web-body-s tw-font-bold tw-mb-0">
                  {grillTemperature?.fahrenheit && (
                    <span>{grillTemperature.fahrenheit} &#730;F </span>
                  )}
                  {grillTemperature?.celsius && <span>/ {grillTemperature.celsius} &#730;C</span>}
                </p>
              </div>
            )}
            {probeTargetTemperature && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <ProbeIcon className="tw-h-12" />
                <p className="tw-text-web-body-s tw-font-bold tw-mb-0">
                  {probeTargetTemperature?.fahrenheit && (
                    <span>{probeTargetTemperature.fahrenheit} &#730;F </span>
                  )}
                  {probeTargetTemperature?.celsius && (
                    <span>/ {probeTargetTemperature.celsius} &#730;C</span>
                  )}
                </p>
              </div>
            )}
            {superSmoke && (
              <div className="tw-flex tw-items-center tw-gap-1">
                <SuperSmokeIcon className="tw-h-12" />
                <p className="tw-text-web-body-s tw-font-bold tw-mb-0">Super Smoke</p>
              </div>
            )}
          </div>
        }
        {Ingredients && (
          <div>
            <h5 className="tra-text-web-sub-3 tw-text-left tw-mb-2 tw-mt-8">
              {t('recipeContent.ingredientsLabel')}
            </h5>
            <ul className="tw-pl-6">
              {Ingredients.map((ingredient, i) => (
                <li key={`ingredient_${i}`}>
                  <RecipeStepIngredient ingredient={ingredient} unitOfMeasure={unitOfMeasure} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div>
        {hasLinkOrAssets && (
          <>
            {youtubeLink && (
              <div className="tw-block tw-pt-4 md:tw-pt-0">
                <div
                  onClick={() =>
                    push({
                      youtubeLink,
                    })
                  }
                  onKeyUp={() =>
                    push({
                      youtubeLink,
                    })
                  }
                  className="tw-cursor-pointer tw-relative tw-aspect-video"
                >
                  <Image
                    src={`https://img.youtube.com/vi/${getYoutubeId(youtubeLink.contentUrl)}/maxresdefault.jpg`}
                    alt={youtubeLink.name}
                    className="tw-rounded"
                    layout="fill"
                    objectFit="cover"
                    unoptimized
                  />

                  <span className="video-play-icon tw-absolute tw-w-[50px] tw-bottom-[10px] tw-right-[10px]">
                    <YoutubePlayIcon color="white" shadow height={'100%'} />
                  </span>
                </div>
              </div>
            )}

            {assets && (
              <div className="tw-relative tw-aspect-[2] tw-mt-4 tw-max-height">
                <Image
                  src={stepImg}
                  alt={assets[0].name}
                  className="tw-rounded"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            )}
          </>
        )}
      </div>
      {proTip?.tip && <ProTip tip={proTip.tip} link={proTip.link} />}
    </div>
  );
};
export default RecipeStep;
